// Story 1.34 — "the Creator Program terms have changed" banner.
//
// THIS IS THE ONLY SURFACE THAT MENTIONS IT, and that is the design rather
// than an omission. A creator is also an ordinary user: they may hold a wallet
// balance and live marketplace listings with other people's money on hold.
// Gating the session — or nagging on the vault, the shop, the wallet — over a
// reworded payout clause would be disproportionate, and on the offers surface
// it would have third-party consequences: a seller who cannot act is a bidder
// whose funds stay held. So the prompt lives on the creator's own dashboard,
// and enforcement happens at the point of a creator WRITE (new application,
// portfolio application, profile publish), never on a read.

import { useState } from "react";

import { Button, Card, CardContent } from "@greenhold/components";

import { useAcceptCreatorTos } from "./useAcceptCreatorTos";

interface Props {
  versionLabel: string | null;
}

export function CreatorTosBanner({ versionLabel }: Props) {
  const accept = useAcceptCreatorTos();
  const [error, setError] = useState<string | null>(null);

  return (
    <Card
      data-testid="creator-tos-banner"
      className="border-brand-gold-500/40 bg-brand-gold-500/5"
    >
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="font-heading text-sm font-semibold text-brand-gold-300">
            The Creator Program terms have been updated
            {versionLabel ? ` (${versionLabel})` : ""}
          </p>
          <p className="font-body text-sm opacity-80">
            Have a read and accept them to keep applying to portfolios and
            publishing profile changes. Your earnings, dossiers and payouts are
            unaffected in the meantime.
          </p>
          <a
            href="/legal/creator-program"
            className="font-body text-sm text-brand-gold-300 underline underline-offset-4"
          >
            Read the Creator Program terms
          </a>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
          <Button
            onClick={() => {
              setError(null);
              accept.mutate(undefined, {
                onError: (e) =>
                  setError(e instanceof Error ? e.message : "Something went wrong."),
              });
            }}
            disabled={accept.isPending}
          >
            {accept.isPending ? "Accepting…" : "Accept and continue"}
          </Button>
          {error ? (
            <p role="alert" className="font-body text-xs text-red-400">
              {error}
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
